import { useState } from "react";
import { Plane, BedDouble, ExternalLink, CalendarDays } from "lucide-react";
import { useLang } from "@/i18n";
import { goUrl } from "@/lib/api";
import { track } from "@/lib/analytics";
import { PartnersBox } from "./PartnersBox";

const SKY = { it: "https://www.skyscanner.it/", en: "https://www.skyscanner.net/", es: "https://www.skyscanner.es/", fr: "https://www.skyscanner.fr/", de: "https://www.skyscanner.de/" };
const BOOKING = { it: "it", en: "en-gb", es: "es", fr: "fr", de: "de" };
const DAY = 86400000;
const yymmdd = (d) => d.slice(2).replaceAll("-", "");
const addDays = (d, n) => new Date(Date.parse(d) + n * DAY).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const Box = ({ testId, linkId, icon: Icon, title, subtitle, tips = [], href, cta, onClick }) => (
  <div data-testid={testId} className="print-break flex flex-col rounded-3xl border border-emerald-100 bg-white p-8">
    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><Icon className="h-5 w-5" /></span>
    <h4 className="mt-5 font-display text-xl font-semibold text-gray-900">{title}</h4>
    <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
    <ul className="mt-5 flex-1 space-y-2 text-sm text-gray-600">
      {tips.map((t, i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{t}</li>)}
    </ul>
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={linkId} onClick={onClick}
      className="no-print mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
      {cta}<ExternalLink className="h-4 w-4" />
    </a>
  </div>
);

const DateField = ({ id, label, value, min, onChange }) => (
  <label className="flex flex-1 flex-col gap-1.5 text-xs font-semibold uppercase tracking-widest text-gray-500">
    {label}
    <input data-testid={id} type="date" value={value} min={min} onChange={(e) => e.target.value && onChange(e.target.value)}
      className="no-print h-11 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium normal-case tracking-normal text-gray-900 outline-none focus:border-emerald-500" />
    <span className="print-only text-base normal-case tracking-normal text-gray-900">{value}</span>
  </label>
);

const TripDates = ({ out, ret, setOut, setRet, adults, route }) => {
  const { t } = useLang();
  const changeOut = (v) => {
    const len = Math.max(1, Math.round((Date.parse(ret) - Date.parse(out)) / DAY));
    setOut(v);
    if (ret <= v) setRet(addDays(v, len));
  };
  return (
    <div data-testid="trip-dates-box" className="mb-6 rounded-3xl border border-emerald-100 bg-emerald-50/60 p-6">
      <p className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <CalendarDays className="h-4 w-4 text-emerald-700" />
        <span data-testid="trip-dates-route" className="font-semibold text-gray-900">{route.from_iata} → {route.to_iata}</span>
        <span>· {t("Viaggiatori: {n}", { n: adults })}</span>
        <span className="text-gray-500">· {t("Date proposte dall'AI: puoi modificarle prima di cercare.")}</span>
      </p>
      <div className="mt-4 flex flex-col gap-4 sm:flex-row">
        <DateField id="outbound-date-input" label={t("Andata")} value={out} min={today()} onChange={changeOut} />
        <DateField id="return-date-input" label={t("Ritorno")} value={ret} min={addDays(out, 1)} onChange={(v) => v > out && setRet(v)} />
      </div>
    </div>
  );
};

export const BookingBoxes = ({ flights = {}, hotels = {}, destination, route = {}, tripId }) => {
  const { t, lang } = useLang();
  const [out, setOut] = useState(route.outbound_date || "");
  const [ret, setRet] = useState(route.return_date || "");
  const adults = route.adults || 1;
  const hasDates = !!(out && ret);
  const city = encodeURIComponent(hotels.city || destination || "");
  const sky = SKY[lang] || SKY.en;
  const skyHref = hasDates && route.from_iata && route.to_iata
    ? `${sky}transport/flights/${route.from_iata.toLowerCase()}/${route.to_iata.toLowerCase()}/${yymmdd(out)}/${yymmdd(ret)}/?adultsv2=${adults}&cabinclass=economy&rtn=1&preferdirects=false`
    : sky;
  const bookingHref = `https://www.booking.com/searchresults.${BOOKING[lang] || "en-gb"}.html?ss=${city}`
    + (hasDates ? `&checkin=${out}&checkout=${ret}&group_adults=${adults}&no_rooms=1&group_children=0` : "");
  return (
  <div data-testid="booking-links-box">
    <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">{t("Voli e alloggi")}</p>
        <h3 className="mt-2 font-display text-2xl font-semibold text-gray-900">{t("Cerca prezzi e disponibilità reali")}</h3>
      </div>
      <p className="text-sm text-gray-500">{t("TripSaver non inventa tariffe: confronta direttamente sui portali.")}</p>
    </div>
    {hasDates && route.from_iata && route.to_iata && <TripDates out={out} ret={ret} setOut={setOut} setRet={setRet} adults={adults} route={route} />}
    <div className="grid gap-6 md:grid-cols-2">
      <Box testId="flights-box" linkId="skyscanner-link" icon={Plane} title={t("Voli e trasporti")}
        subtitle={[flights.from, flights.to].filter(Boolean).join(" → ")} tips={flights.tips}
        href={goUrl(tripId, "skyscanner", skyHref)} cta={t("Cerca voli su Skyscanner")}
        onClick={() => track("affiliate_click", { partner: "Skyscanner", category: "flights", platform: "travelpayouts" })} />
      <Box testId="hotels-box" linkId="booking-link" icon={BedDouble} title={t("Alloggi")}
        subtitle={hotels.city || destination} tips={hotels.tips}
        href={goUrl(tripId, "booking", bookingHref)} cta={t("Trova alloggi su Booking.com")}
        onClick={() => track("affiliate_click", { partner: "Booking.com", category: "hotels", platform: "cj" })} />
    </div>
    <PartnersBox tripId={tripId} dates={hasDates ? { a: out, r: ret } : null} />
  </div>
  );
};
